<template>
  <Transition name="modal-fade">
    <div class="bulk-modal-overlay" @click.self="$emit('close')">
      <Transition name="modal-scale">
        <div class="bulk-modal" @click.stop>
          <!-- Header avec gradient animé -->
          <div class="bulk-modal-header">
            <div class="header-background">
              <div class="gradient-orb orb-1"></div>
              <div class="gradient-orb orb-2"></div>
              <div class="gradient-orb orb-3"></div>
            </div>
            <div class="header-content">
              <div class="header-icon">
                <i class="fas fa-layer-group"></i>
                <div class="icon-ring"></div>
              </div>
              <div class="header-text">
                <h2>Ajout en Lot</h2>
                <p>Créez plusieurs produits avec des identifiants uniques</p>
              </div>
            </div>
            <button @click="$emit('close')" class="close-button">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Corps du modal -->
          <div class="bulk-modal-body">
            <!-- Stepper visuel -->
            <div class="stepper">
              <div 
                v-for="(step, index) in steps" 
                :key="index"
                class="step"
                :class="{ 
                  active: currentStep === index, 
                  completed: currentStep > index,
                  clickable: index <= maxReachedStep
                }"
                @click="goToStep(index)"
              >
                <div class="step-indicator">
                  <div class="step-number">
                    <i v-if="currentStep > index" class="fas fa-check"></i>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <div class="step-pulse" v-if="currentStep === index"></div>
                </div>
                <div class="step-info">
                  <span class="step-title">{{ step.title }}</span>
                  <span class="step-subtitle">{{ step.subtitle }}</span>
                </div>
                <div class="step-connector" v-if="index < steps.length - 1"></div>
              </div>
            </div>

            <!-- Contenu des étapes -->
            <div class="step-content">
              <TransitionGroup name="slide-fade" mode="out-in">
                <!-- Étape 1: Informations Produit -->
                <div v-if="currentStep === 0" key="step1" class="step-panel">
                  <div class="panel-header">
                    <div class="panel-icon info">
                      <i class="fas fa-box"></i>
                    </div>
                    <div class="panel-title">
                      <h3>Informations du Produit</h3>
                      <p>Définissez les caractéristiques communes à tous les produits</p>
                    </div>
                  </div>

                  <div class="form-grid">
                    <div class="form-field span-2">
                      <label>
                        <i class="fas fa-tag"></i>
                        Désignation
                        <span class="required">*</span>
                      </label>
                      <div class="input-wrapper" :class="{ focused: focusedField === 'designation', error: errors.designation }">
                        <input
                          type="text"
                          v-model="form.designation"
                          placeholder="Ex: GPS Tracker Pro 4G"
                          @focus="focusedField = 'designation'"
                          @blur="focusedField = null"
                        />
                        <div class="input-border"></div>
                      </div>
                      <span v-if="errors.designation" class="field-error">
                        <i class="fas fa-exclamation-circle"></i>
                        {{ errors.designation }}
                      </span>
                    </div>

                    <div class="form-field">
                      <label>
                        <i class="fas fa-folder"></i>
                        Catégorie
                        <span class="required">*</span>
                      </label>
                      <div class="category-selector">
                        <div 
                          v-for="cat in availableCategories" 
                          :key="cat.value"
                          class="category-option"
                          :class="{ selected: form.categorie === cat.value }"
                          @click="selectCategory(cat.value)"
                        >
                          <div class="category-icon" :style="{ background: cat.gradient }">
                            <i :class="cat.icon"></i>
                          </div>
                          <span>{{ cat.label }}</span>
                          <div class="check-mark" v-if="form.categorie === cat.value">
                            <i class="fas fa-check"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="form-field">
                      <label>
                        <i class="fas fa-warehouse"></i>
                        Emplacement
                      </label>
                      <div class="select-wrapper" :class="{ focused: focusedField === 'emplacement' }">
                        <select 
                          v-model="form.emplacement"
                          @focus="focusedField = 'emplacement'"
                          @blur="focusedField = null"
                        >
                          <option value="magasin_central">🏢 Magasin Central</option>
                          <option value="entrepot_a">🏭 Entrepôt A</option>
                          <option value="entrepot_b">🏭 Entrepôt B</option>
                        </select>
                        <i class="fas fa-chevron-down select-arrow"></i>
                      </div>
                    </div>

                    <div class="form-field">
                      <label>
                        <i class="fas fa-industry"></i>
                        Marque
                      </label>
                      <div class="input-wrapper" :class="{ focused: focusedField === 'marque' }">
                        <input
                          type="text"
                          v-model="form.marque"
                          placeholder="Ex: Teltonika"
                          @focus="focusedField = 'marque'"
                          @blur="focusedField = null"
                        />
                        <div class="input-border"></div>
                      </div>
                    </div>

                    <div class="form-field">
                      <label>
                        <i class="fas fa-microchip"></i>
                        Modèle
                      </label>
                      <div class="input-wrapper" :class="{ focused: focusedField === 'modele' }">
                        <input
                          type="text"
                          v-model="form.modele"
                          placeholder="Ex: FMB920"
                          @focus="focusedField = 'modele'"
                          @blur="focusedField = null"
                        />
                        <div class="input-border"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Étape 2: Prix -->
                <div v-if="currentStep === 1" key="step2" class="step-panel">
                  <div class="panel-header">
                    <div class="panel-icon price">
                      <i class="fas fa-coins"></i>
                    </div>
                    <div class="panel-title">
                      <h3>Tarification</h3>
                      <p>Définissez les prix d'achat et de vente</p>
                    </div>
                  </div>

                  <div class="price-cards">
                    <div class="price-card purchase">
                      <div class="price-card-header">
                        <i class="fas fa-shopping-cart"></i>
                        <span>Prix d'Achat</span>
                      </div>
                      <div class="price-input-group">
                        <input
                          type="number"
                          v-model.number="form.prixAchat"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <span class="currency">TND</span>
                      </div>
                    </div>

                    <div class="price-arrow">
                      <i class="fas fa-arrow-right"></i>
                    </div>

                    <div class="price-card sale">
                      <div class="price-card-header">
                        <i class="fas fa-hand-holding-usd"></i>
                        <span>Prix de Vente HT</span>
                      </div>
                      <div class="price-input-group">
                        <input
                          type="number"
                          v-model.number="form.prixVente"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <span class="currency">TND</span>
                      </div>
                    </div>
                  </div>

                  <!-- Message d'erreur si prix de vente < prix d'achat -->
                  <div class="price-error-message" v-if="priceError">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>{{ priceError }}</span>
                  </div>

                  <div class="profit-indicator" v-if="form.prixVente > 0 && form.prixAchat > 0 && !priceError">
                    <div class="profit-bar">
                      <div class="profit-fill" :style="{ width: profitPercentage + '%' }"></div>
                    </div>
                    <div class="profit-info">
                      <span class="profit-label">Marge bénéficiaire</span>
                      <span class="profit-value" :class="{ positive: profit > 0, negative: profit < 0 }">
                        {{ profit > 0 ? '+' : '' }}{{ profit.toFixed(2) }} TND ({{ profitPercentage.toFixed(1) }}%)
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Étape 3: Identifiants -->
                <div v-if="currentStep === 2" key="step3" class="step-panel">
                  <div class="panel-header">
                    <div class="panel-icon identifiers">
                      <i class="fas fa-barcode"></i>
                    </div>
                    <div class="panel-title">
                      <h3>{{ form.categorie === 'SIM' ? 'Numéros ICCID' : 'Numéros IMEI' }}</h3>
                      <p>Entrez un identifiant par ligne</p>
                    </div>
                  </div>

                  <div class="identifiers-container">
                    <div class="identifiers-input-section">
                      <div class="input-header">
                        <div class="input-label">
                          <i :class="form.categorie === 'SIM' ? 'fas fa-sim-card' : 'fas fa-satellite-dish'"></i>
                          <span>{{ form.categorie === 'SIM' ? 'Liste des ICCID' : 'Liste des IMEI' }}</span>
                        </div>
                        <div class="input-hint">
                          <i class="fas fa-info-circle"></i>
                          {{ form.categorie === 'SIM' ? '19-20 chiffres par ICCID' : '15 chiffres par IMEI' }}
                        </div>
                      </div>
                      <div class="textarea-wrapper" :class="{ focused: focusedField === 'identifiers', error: errors.identifiers }">
                        <div class="line-numbers">
                          <span v-for="n in lineCount" :key="n">{{ n }}</span>
                        </div>
                        <textarea
                          v-model="identifiersText"
                          :placeholder="identifierPlaceholder"
                          @input="parseIdentifiers"
                          @focus="focusedField = 'identifiers'"
                          @blur="focusedField = null"
                          spellcheck="false"
                        ></textarea>
                      </div>
                      <span v-if="errors.identifiers" class="field-error">
                        <i class="fas fa-exclamation-circle"></i>
                        {{ errors.identifiers }}
                      </span>
                    </div>

                    <div class="identifiers-preview-section" v-if="parsedIdentifiers.length > 0">
                      <div class="preview-stats">
                        <div class="stat valid">
                          <div class="stat-icon">
                            <i class="fas fa-check-circle"></i>
                          </div>
                          <div class="stat-info">
                            <span class="stat-value">{{ validIdentifiers.length }}</span>
                            <span class="stat-label">Valides</span>
                          </div>
                        </div>
                        <div class="stat invalid" v-if="invalidIdentifiers.length > 0">
                          <div class="stat-icon">
                            <i class="fas fa-times-circle"></i>
                          </div>
                          <div class="stat-info">
                            <span class="stat-value">{{ invalidIdentifiers.length }}</span>
                            <span class="stat-label">Invalides</span>
                          </div>
                        </div>
                        <div class="stat duplicate" v-if="duplicateIdentifiers.length > 0">
                          <div class="stat-icon">
                            <i class="fas fa-exclamation-triangle"></i>
                          </div>
                          <div class="stat-info">
                            <span class="stat-value">{{ duplicateIdentifiers.length }}</span>
                            <span class="stat-label">Déjà existants</span>
                          </div>
                        </div>
                        <div class="stat checking" v-if="checkingIdentifiers">
                          <div class="stat-icon">
                            <i class="fas fa-spinner fa-spin"></i>
                          </div>
                          <div class="stat-info">
                            <span class="stat-label">Vérification...</span>
                          </div>
                        </div>
                      </div>

                      <!-- Alerte pour les IMEI existants -->
                      <div v-if="duplicateIdentifiers.length > 0" class="duplicate-alert">
                        <i class="fas fa-exclamation-triangle"></i>
                        <span>{{ duplicateIdentifiers.length }} {{ form.categorie === 'SIM' ? 'ICCID' : 'IMEI' }} déjà existant(s) dans la base de données</span>
                      </div>

                      <div class="preview-list">
                        <TransitionGroup name="list">
                          <div 
                            v-for="(item, index) in parsedIdentifiers.slice(0, 10)" 
                            :key="item.value + index"
                            class="preview-item"
                            :class="{ valid: item.valid && !item.exists, invalid: !item.valid && !item.exists, duplicate: item.exists }"
                          >
                            <div class="item-status">
                              <i :class="item.exists ? 'fas fa-exclamation-triangle' : (item.valid ? 'fas fa-check' : 'fas fa-times')"></i>
                            </div>
                            <span class="item-value">{{ item.value }}</span>
                            <span v-if="item.exists" class="item-error duplicate-error">
                              <i class="fas fa-database"></i> Déjà existant
                            </span>
                            <span v-else-if="!item.valid" class="item-error">{{ item.error }}</span>
                          </div>
                        </TransitionGroup>
                        <div v-if="parsedIdentifiers.length > 10" class="more-items">
                          <i class="fas fa-ellipsis-h"></i>
                          <span>+{{ parsedIdentifiers.length - 10 }} autres</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
            </div>
          </div>

          <!-- Footer avec navigation -->
          <div class="bulk-modal-footer">
            <div class="footer-left">
              <div class="progress-indicator">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
                </div>
                <span class="progress-text">Étape {{ currentStep + 1 }} / {{ steps.length }}</span>
              </div>
            </div>
            <div class="footer-actions">
              <button 
                v-if="currentStep > 0"
                @click="previousStep" 
                class="btn-nav btn-prev"
              >
                <i class="fas fa-arrow-left"></i>
                <span>Précédent</span>
              </button>
              <button 
                v-if="currentStep < steps.length - 1"
                @click="nextStep" 
                class="btn-nav btn-next"
                :disabled="!canProceed"
              >
                <span>Suivant</span>
                <i class="fas fa-arrow-right"></i>
              </button>
              <button 
                v-else
                @click="handleSubmit" 
                class="btn-submit"
                :disabled="loading || validIdentifiers.length === 0"
              >
                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-rocket"></i>
                <span>Créer {{ validIdentifiers.length }} Produit{{ validIdentifiers.length > 1 ? 's' : '' }}</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import api from '@/services/api'

export default {
  name: 'BulkProductModal',
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const loading = ref(false)
    const errors = reactive({})
    const categories = ref([])
    const loadingCategories = ref(false)
    const identifiersText = ref('')
    const parsedIdentifiers = ref([])
    const currentStep = ref(0)
    const maxReachedStep = ref(0)
    const focusedField = ref(null)
    const checkingIdentifiers = ref(false)
    const existingIdentifiersMap = ref({})
    
    const form = reactive({
      designation: '',
      categorie: 'GPS',
      marque: '',
      modele: '',
      description: '',
      prixAchat: 0,
      prixVente: 0,
      tva: 19,
      seuilAlerte: 5,
      unite: 'pièce',
      emplacement: 'magasin_central',
      fournisseur: ''
    })

    const steps = [
      { title: 'Produit', subtitle: 'Informations' },
      { title: 'Prix', subtitle: 'Tarification' },
      { title: 'Identifiants', subtitle: 'IMEI/ICCID' }
    ]

    const availableCategories = [
      { 
        value: 'GPS', 
        label: 'GPS', 
        icon: 'fas fa-satellite-dish',
        gradient: 'linear-gradient(135deg, #001BB7 0%, #0046FF 100%)'
      },
      { 
        value: 'SIM', 
        label: 'SIM', 
        icon: 'fas fa-sim-card',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      }
    ]

    const progressPercentage = computed(() => ((currentStep.value + 1) / steps.length) * 100)
    
    const profit = computed(() => form.prixVente - form.prixAchat)
    
    const profitPercentage = computed(() => {
      if (form.prixAchat <= 0) return 0
      return ((form.prixVente - form.prixAchat) / form.prixAchat) * 100
    })

    const lineCount = computed(() => {
      const lines = identifiersText.value.split('\n').length
      return Math.max(lines, 5)
    })

    const identifierPlaceholder = computed(() => {
      if (form.categorie === 'SIM') {
        return '89332404000000000001\n89332404000000000002\n89332404000000000003'
      }
      return '123456789012345\n123456789012346\n123456789012347'
    })

    const validIdentifiers = computed(() => parsedIdentifiers.value.filter(item => item.valid && !item.exists))
    const invalidIdentifiers = computed(() => parsedIdentifiers.value.filter(item => !item.valid || item.exists))
    const duplicateIdentifiers = computed(() => parsedIdentifiers.value.filter(item => item.exists))

    const canProceed = computed(() => {
      if (currentStep.value === 0) {
        return form.designation.trim() && form.categorie
      }
      if (currentStep.value === 1) {
        // Le prix de vente doit être supérieur ou égal au prix d'achat
        return form.prixVente >= form.prixAchat
      }
      return validIdentifiers.value.length > 0
    })
    
    // Message d'erreur pour le prix
    const priceError = computed(() => {
      if (form.prixVente < form.prixAchat && form.prixVente > 0) {
        return 'Le prix de vente doit être supérieur ou égal au prix d\'achat'
      }
      return ''
    })

    const selectCategory = (value) => {
      form.categorie = value
      existingIdentifiersMap.value = {}
      parseIdentifiers()
    }

    const goToStep = (index) => {
      if (index <= maxReachedStep.value) {
        currentStep.value = index
      }
    }

    const nextStep = () => {
      if (canProceed.value && currentStep.value < steps.length - 1) {
        currentStep.value++
        maxReachedStep.value = Math.max(maxReachedStep.value, currentStep.value)
      }
    }

    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--
      }
    }

    // Vérifier les identifiants existants dans la base de données
    const checkExistingIdentifiers = async (identifiers) => {
      if (identifiers.length === 0) return
      
      checkingIdentifiers.value = true
      try {
        const type = form.categorie === 'SIM' ? 'iccid' : 'imei'
        const response = await api.post('/stock/check-identifiers', {
          identifiers,
          type
        })
        
        if (response.data.success) {
          const existingMap = {}
          response.data.data.existingIdentifiers.forEach(item => {
            existingMap[item.identifier] = item
          })
          existingIdentifiersMap.value = existingMap
          
          // Mettre à jour les identifiants parsés avec l'info d'existence
          parsedIdentifiers.value = parsedIdentifiers.value.map(item => ({
            ...item,
            exists: !!existingMap[item.value],
            existingInfo: existingMap[item.value] || null,
            error: existingMap[item.value] ? 'Déjà existant' : item.error
          }))
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des identifiants:', error)
      } finally {
        checkingIdentifiers.value = false
      }
    }

    // Debounce pour la vérification
    let checkTimeout = null
    const debouncedCheck = (identifiers) => {
      if (checkTimeout) clearTimeout(checkTimeout)
      checkTimeout = setTimeout(() => {
        checkExistingIdentifiers(identifiers)
      }, 500)
    }

    const parseIdentifiers = () => {
      const lines = identifiersText.value.split('\n').filter(line => line.trim())
      const validIdentifiersList = []
      
      parsedIdentifiers.value = lines.map(line => {
        const value = line.trim()
        let valid = false
        let error = ''
        
        if (form.categorie === 'GPS') {
          if (/^[0-9]{15}$/.test(value)) {
            valid = true
            validIdentifiersList.push(value)
          } else if (!/^[0-9]+$/.test(value)) {
            error = 'Chiffres uniquement'
          } else {
            error = `${value.length}/15 chiffres`
          }
        } else if (form.categorie === 'SIM') {
          if (/^[0-9]{19,20}$/.test(value)) {
            valid = true
            validIdentifiersList.push(value)
          } else if (!/^[0-9]+$/.test(value)) {
            error = 'Chiffres uniquement'
          } else {
            error = `${value.length}/19-20 chiffres`
          }
        }
        
        // Vérifier si déjà marqué comme existant
        const exists = !!existingIdentifiersMap.value[value]
        
        return { 
          value, 
          valid: valid && !exists, 
          error: exists ? 'Déjà existant' : error,
          exists,
          existingInfo: existingIdentifiersMap.value[value] || null
        }
      })
      
      // Vérifier les identifiants valides dans la base de données
      if (validIdentifiersList.length > 0) {
        debouncedCheck(validIdentifiersList)
      }
    }

    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key])
      let isValid = true
      
      if (!form.designation.trim()) {
        errors.designation = 'La désignation est requise'
        isValid = false
      }
      
      if (!form.categorie) {
        errors.categorie = 'La catégorie est requise'
        isValid = false
      }
      
      if (validIdentifiers.value.length === 0) {
        errors.identifiers = `Veuillez entrer au moins un ${form.categorie === 'SIM' ? 'ICCID' : 'IMEI'} valide et non existant`
        isValid = false
      }
      
      return isValid
    }

    const handleSubmit = async () => {
      if (!validateForm()) return

      loading.value = true
      try {
        const payload = {
          ...form,
          imeiList: form.categorie === 'GPS' ? validIdentifiers.value.map(i => i.value) : [],
          iccidList: form.categorie === 'SIM' ? validIdentifiers.value.map(i => i.value) : []
        }

        const response = await api.post('/stock/products/bulk', payload)
        
        if (response.data.success) {
          emit('save', response.data.data)
        }
      } catch (error) {
        console.error('Erreur lors de la création en lot:', error)
        if (error.response?.data?.message) {
          errors.identifiers = error.response.data.message
        }
      } finally {
        loading.value = false
      }
    }

    const loadCategories = async () => {
      loadingCategories.value = true
      try {
        const response = await api.get('/categories', { params: { actif: true } })
        categories.value = response.data.data
      } catch (error) {
        console.error('Erreur chargement catégories:', error)
      } finally {
        loadingCategories.value = false
      }
    }

    onMounted(() => {
      loadCategories()
    })

    return {
      loading,
      errors,
      form,
      categories,
      loadingCategories,
      identifiersText,
      parsedIdentifiers,
      currentStep,
      maxReachedStep,
      focusedField,
      steps,
      availableCategories,
      progressPercentage,
      profit,
      profitPercentage,
      priceError,
      lineCount,
      identifierPlaceholder,
      validIdentifiers,
      invalidIdentifiers,
      duplicateIdentifiers,
      canProceed,
      checkingIdentifiers,
      selectCategory,
      goToStep,
      nextStep,
      previousStep,
      parseIdentifiers,
      validateForm,
      handleSubmit
    }
  }
}
</script>


<style scoped>
/* ===== ANIMATIONS ===== */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active {
  animation: modalEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-scale-leave-active {
  animation: modalLeave 0.25s ease-in;
}

@keyframes modalEnter {
  0% { opacity: 0; transform: scale(0.9) translateY(20px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes modalLeave {
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.95); }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}
.list-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}
.list-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* ===== OVERLAY ===== */
.bulk-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

/* ===== MODAL CONTAINER ===== */
.bulk-modal {
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== HEADER ===== */
.bulk-modal-header {
  position: relative;
  padding: 1.75rem 2rem;
  background: linear-gradient(135deg, #001BB7 0%, #0046FF 100%);
  overflow: hidden;
}

.header-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.4;
  animation: float 8s ease-in-out infinite;
}

.orb-1 {
  width: 200px;
  height: 200px;
  background: #3366FF;
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.orb-2 {
  width: 150px;
  height: 150px;
  background: #0046FF;
  bottom: -30px;
  left: 20%;
  animation-delay: -2s;
}

.orb-3 {
  width: 100px;
  height: 100px;
  background: #4D7CFF;
  top: 20px;
  left: 40%;
  animation-delay: -4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
}

.header-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  z-index: 1;
}

.header-icon {
  position: relative;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.icon-ring {
  position: absolute;
  inset: -4px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.2); opacity: 0; }
}

.header-text h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  letter-spacing: -0.02em;
}

.header-text p {
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.close-button {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: rotate(90deg);
}

/* ===== BODY ===== */
.bulk-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
}

/* ===== STEPPER ===== */
.stepper {
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 2rem;
  padding: 0 1rem;
}

.step {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  cursor: default;
  transition: all 0.3s ease;
}

.step.clickable {
  cursor: pointer;
}

.step-indicator {
  position: relative;
}

.step-number {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #f1f5f9;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  color: #94a3b8;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: linear-gradient(135deg, #001BB7 0%, #0046FF 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.4);
}

.step.completed .step-number {
  background: #10b981;
  border-color: transparent;
  color: white;
}

.step-pulse {
  position: absolute;
  inset: -6px;
  border-radius: 18px;
  border: 2px solid #001BB7;
  animation: step-pulse 2s ease-out infinite;
}

@keyframes step-pulse {
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(1.3); opacity: 0; }
}

.step-info {
  display: flex;
  flex-direction: column;
}

.step-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #334155;
}

.step-subtitle {
  font-size: 0.75rem;
  color: #94a3b8;
}

.step.active .step-title {
  color: #001BB7;
}

.step-connector {
  width: 60px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 0.5rem;
  position: relative;
  overflow: hidden;
}

.step.completed .step-connector::after {
  content: '';
  position: absolute;
  inset: 0;
  background: #10b981;
  animation: connector-fill 0.5s ease forwards;
}

@keyframes connector-fill {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

/* ===== STEP PANELS ===== */
.step-content {
  min-height: 350px;
}

.step-panel {
  background: #f8fafc;
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.panel-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
}

.panel-icon.info {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.panel-icon.price {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.panel-icon.identifiers {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.panel-title h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.panel-title p {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

/* ===== FORM GRID ===== */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field.span-2 {
  grid-column: span 2;
}

.form-field label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
}

.form-field label i {
  color: #94a3b8;
  font-size: 0.8rem;
}

.form-field label .required {
  color: #ef4444;
}

.input-wrapper {
  position: relative;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.input-wrapper input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: transparent;
}

.input-wrapper input:focus {
  outline: none;
  border-color: #001BB7;
}

.input-wrapper.focused {
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
}

.input-wrapper.error input {
  border-color: #ef4444;
}

.input-border {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #001BB7, #0046FF);
  transition: all 0.3s ease;
}

.input-wrapper.focused .input-border {
  left: 0;
  width: 100%;
}

.field-error {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: #ef4444;
}

/* ===== CATEGORY SELECTOR ===== */
.category-selector {
  display: flex;
  gap: 1rem;
}

.category-option {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.category-option:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.category-option.selected {
  border-color: #001BB7;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05) 0%, rgba(0, 70, 255, 0.05) 100%);
}

.category-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.category-option span {
  font-weight: 600;
  color: #334155;
}

.check-mark {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #001BB7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  animation: pop-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes pop-in {
  0% { transform: scale(0); }
  100% { transform: scale(1); }
}

/* ===== SELECT WRAPPER ===== */
.select-wrapper {
  position: relative;
}

.select-wrapper select {
  width: 100%;
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
}

.select-wrapper select:focus {
  outline: none;
  border-color: #001BB7;
}

.select-wrapper.focused {
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
}

.select-arrow {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
  transition: transform 0.2s ease;
}

.select-wrapper.focused .select-arrow {
  transform: translateY(-50%) rotate(180deg);
}

/* ===== PRICE CARDS ===== */
.price-cards {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.price-card {
  flex: 1;
  background: white;
  border-radius: 12px;
  padding: 0.875rem;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
  min-width: 0;
}

.price-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.price-card.purchase {
  border-color: #fbbf24;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.price-card.sale {
  border-color: #34d399;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.price-card-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}

.price-card.purchase .price-card-header i {
  color: #f59e0b;
}

.price-card.sale .price-card-header i {
  color: #10b981;
}

.price-input-group {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.price-input-group input {
  flex: 1;
  width: 100%;
  min-width: 0;
  padding: 0.5rem 0.625rem;
  border: none;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  text-align: right;
}

.price-input-group input:focus {
  outline: none;
  background: white;
}

.price-input-group .currency {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.price-arrow {
  width: 32px;
  height: 32px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  flex-shrink: 0;
}

/* ===== PRICE ERROR MESSAGE ===== */
.price-error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 10px;
  color: #dc2626;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1rem;
  animation: shake 0.5s ease-in-out;
}

.price-error-message i {
  font-size: 1rem;
  color: #ef4444;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}

/* ===== PROFIT INDICATOR ===== */
.profit-indicator {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid #e2e8f0;
}

.profit-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.profit-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.profit-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.profit-label {
  font-size: 0.85rem;
  color: #64748b;
}

.profit-value {
  font-weight: 600;
  font-size: 0.9rem;
}

.profit-value.positive {
  color: #10b981;
}

.profit-value.negative {
  color: #ef4444;
}

/* ===== IDENTIFIERS SECTION ===== */
.identifiers-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.identifiers-input-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #334155;
}

.input-label i {
  color: #001BB7;
}

.input-hint {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

.textarea-wrapper {
  display: flex;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.textarea-wrapper.focused {
  border-color: #001BB7;
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
}

.textarea-wrapper.error {
  border-color: #ef4444;
}

.line-numbers {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 0.75rem;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8rem;
  color: #94a3b8;
  line-height: 1.6;
  user-select: none;
}

.textarea-wrapper textarea {
  flex: 1;
  padding: 0.875rem 1rem;
  border: none;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  resize: none;
  min-height: 200px;
}

.textarea-wrapper textarea:focus {
  outline: none;
}

.textarea-wrapper textarea::placeholder {
  color: #cbd5e1;
}

/* ===== PREVIEW SECTION ===== */
.identifiers-preview-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-stats {
  display: flex;
  gap: 1rem;
}

.stat {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  background: white;
  border: 1px solid #e2e8f0;
}

.stat.valid {
  border-color: #86efac;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

.stat.invalid {
  border-color: #fca5a5;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.stat.valid .stat-icon {
  background: #10b981;
  color: white;
}

.stat.invalid .stat-icon {
  background: #ef4444;
  color: white;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 0.75rem;
  color: #64748b;
}

.preview-list {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  max-height: 200px;
  overflow-y: auto;
}

.preview-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.85rem;
}

.preview-item:last-child {
  border-bottom: none;
}

.preview-item.valid {
  background: #f0fdf4;
}

.preview-item.invalid {
  background: #fef2f2;
}

.item-status {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.preview-item.valid .item-status {
  background: #10b981;
  color: white;
}

.preview-item.invalid .item-status {
  background: #ef4444;
  color: white;
}

.item-value {
  flex: 1;
  color: #334155;
}

.item-error {
  font-size: 0.75rem;
  color: #ef4444;
  font-family: system-ui, sans-serif;
}

.more-items {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: #64748b;
  font-size: 0.85rem;
  background: #f8fafc;
}

/* ===== FOOTER ===== */
.bulk-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.progress-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.progress-bar {
  width: 120px;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #001BB7, #0046FF);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-prev {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-prev:hover {
  background: #f8fafc;
  color: #334155;
}

.btn-next {
  background: linear-gradient(135deg, #001BB7 0%, #0046FF 100%);
  color: white;
}

.btn-next:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.4);
}

.btn-next:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-submit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  transition: all 0.2s ease;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .bulk-modal {
    max-height: 95vh;
    border-radius: 20px 20px 0 0;
    margin-top: auto;
  }

  .bulk-modal-header {
    padding: 1.25rem 1.5rem;
  }

  .bulk-modal-body {
    padding: 1rem 1.5rem;
  }

  .stepper {
    flex-direction: column;
    gap: 0.5rem;
  }

  .step-connector {
    display: none;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field.span-2 {
    grid-column: span 1;
  }

  .category-selector {
    flex-direction: column;
  }

  .price-cards {
    flex-direction: column;
  }

  .price-arrow {
    transform: rotate(90deg);
  }

  .identifiers-container {
    grid-template-columns: 1fr;
  }

  .bulk-modal-footer {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 1.5rem;
  }

  .footer-actions {
    width: 100%;
    justify-content: stretch;
  }

  .btn-nav,
  .btn-submit {
    flex: 1;
    justify-content: center;
  }
}

/* ===== DUPLICATE IDENTIFIERS STYLES ===== */
.stat.duplicate {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.stat.duplicate .stat-icon {
  background: #f59e0b;
  color: white;
}

.stat.checking {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
}

.stat.checking .stat-icon {
  background: #6366f1;
  color: white;
}

.duplicate-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  margin-bottom: 1rem;
  color: #92400e;
  font-size: 0.9rem;
  font-weight: 500;
}

.duplicate-alert i {
  color: #f59e0b;
  font-size: 1.1rem;
}

.preview-item.duplicate {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #f59e0b;
}

.preview-item.duplicate .item-status {
  background: #f59e0b;
  color: white;
}

.preview-item.duplicate .item-value {
  color: #92400e;
}

.duplicate-error {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #92400e !important;
  background: rgba(245, 158, 11, 0.2);
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-weight: 500;
}

.duplicate-error i {
  font-size: 0.75rem;
}
</style>
